import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import { ArrowLeft, Printer, Eye, Sparkles, Trash2, Loader2 } from 'lucide-react';
import api from '../api/axios';
import { aiTemplateKey, resolveTemplate } from '../utils/qrAiTemplate';

const FRONTEND_URL = window.location.origin;

const TEMPLATES = {
  classique: {
    label: 'Classique',
    desc: 'Bois & cuir, elegant',
    preview: 'bg-[#2c1810]',
    qrColor: '#2c1810',
    css: (size) => `
      .qr-cell{width:${size==='large'?'95mm':'63mm'};height:${size==='large'?'133mm':'88mm'};border:1px dashed #bbb;display:flex;align-items:center;justify-content:center;page-break-inside:avoid;break-inside:avoid}
      .qr-inner{display:flex;flex-direction:column;align-items:center;text-align:center;padding:${size==='large'?'5mm':'3mm'};width:100%;height:100%;background:white;border:2px solid #d4c4b0}
      .qr-logo{height:${size==='large'?'16mm':'10mm'};margin:${size==='large'?'4mm':'2mm'} 0}
      .qr-line{width:50%;height:1px;background:#d4c4b0;margin:${size==='large'?'2mm':'1mm'} 0}
      .qr-wrap{border:2px solid #2c1810;border-radius:6px;padding:${size==='large'?'3mm':'2mm'};margin:${size==='large'?'3mm':'2mm'} 0}
      .qr-wrap svg{width:${size==='large'?'30mm':'20mm'}!important;height:${size==='large'?'30mm':'20mm'}!important}
      .qr-tnum{font-family:Georgia,serif;font-size:${size==='large'?'20pt':'13pt'};font-weight:bold;color:#2c1810;margin:${size==='large'?'2mm':'1mm'} 0}
      .qr-txt{font-size:${size==='large'?'8pt':'6pt'};color:#555;line-height:1.3}
      .qr-txt b{color:#2c1810;font-size:${size==='large'?'9pt':'7pt'};display:block}
      .qr-url{font-size:${size==='large'?'6pt':'5pt'};color:#aaa;margin-top:auto;padding-top:1mm}
    `,
    html: (t, num) => `<div class="qr-inner"><img src="/logo-taverne.png" class="qr-logo"/><div class="qr-line"></div><div class="qr-wrap" id="svg-t${num}"></div><div class="qr-tnum">Table ${num}</div><div class="qr-txt"><b>Scannez pour commander</b>Ouvrez l'appareil photo</div><div class="qr-url">latavernecay.fr</div></div>`,
  },
  ardoise: {
    label: 'Ardoise',
    desc: 'Style tableau noir bistrot',
    preview: 'bg-[#2a2a2a]',
    qrColor: '#ffffff',
    css: (size) => `
      .qr-cell{width:${size==='large'?'95mm':'63mm'};height:${size==='large'?'133mm':'88mm'};border:1px dashed #bbb;display:flex;align-items:center;justify-content:center;page-break-inside:avoid;break-inside:avoid}
      .qr-inner{display:flex;flex-direction:column;align-items:center;text-align:center;padding:${size==='large'?'6mm':'4mm'};width:100%;height:100%;background:#2a2a2a;border:3px solid #5a4a3a;position:relative}
      .qr-inner::before{content:'';position:absolute;inset:3px;border:1px dashed rgba(255,255,255,0.15)}
      .qr-logo{height:${size==='large'?'14mm':'9mm'};margin:${size==='large'?'5mm':'3mm'} 0;filter:brightness(2)}
      .qr-deco{color:rgba(255,255,255,0.3);font-size:${size==='large'?'14pt':'9pt'};letter-spacing:4px;font-family:Georgia,serif}
      .qr-wrap{background:white;border-radius:8px;padding:${size==='large'?'3mm':'2mm'};margin:${size==='large'?'3mm':'2mm'} 0}
      .qr-wrap svg{width:${size==='large'?'28mm':'18mm'}!important;height:${size==='large'?'28mm':'18mm'}!important}
      .qr-tnum{font-family:'Courier New',monospace;font-size:${size==='large'?'24pt':'15pt'};font-weight:bold;color:white;margin:${size==='large'?'2mm':'1mm'} 0;text-shadow:0 0 8px rgba(255,200,100,0.3)}
      .qr-txt{font-size:${size==='large'?'8pt':'6pt'};color:rgba(255,255,255,0.7);line-height:1.4;font-style:italic}
      .qr-txt b{color:white;font-size:${size==='large'?'10pt':'7pt'};display:block;font-style:normal;letter-spacing:1px}
      .qr-url{font-size:${size==='large'?'6pt':'5pt'};color:rgba(255,255,255,0.3);margin-top:auto;padding-top:1mm}
    `,
    html: (t, num) => `<div class="qr-inner"><div class="qr-deco">~ ~ ~</div><img src="/logo-taverne.png" class="qr-logo"/><div class="qr-deco">~ ~ ~</div><div class="qr-wrap" id="svg-t${num}"></div><div class="qr-tnum">Table ${num}</div><div class="qr-txt"><b>Scannez pour commander</b>Ouvrez l'appareil photo</div><div class="qr-url">latavernecay.fr</div></div>`,
  },
  minimaliste: {
    label: 'Minimaliste',
    desc: 'Epure, moderne, blanc',
    preview: 'bg-white border-2 border-stone-200',
    qrColor: '#1a1a1a',
    css: (size) => `
      .qr-cell{width:${size==='large'?'95mm':'63mm'};height:${size==='large'?'133mm':'88mm'};border:1px dashed #ddd;display:flex;align-items:center;justify-content:center;page-break-inside:avoid;break-inside:avoid}
      .qr-inner{display:flex;flex-direction:column;align-items:center;text-align:center;padding:${size==='large'?'8mm':'5mm'};width:100%;height:100%;background:white}
      .qr-logo{height:${size==='large'?'12mm':'8mm'};margin:${size==='large'?'6mm':'4mm'} 0;opacity:0.7}
      .qr-wrap{margin:${size==='large'?'6mm':'3mm'} 0}
      .qr-wrap svg{width:${size==='large'?'35mm':'22mm'}!important;height:${size==='large'?'35mm':'22mm'}!important}
      .qr-tnum{font-family:'Helvetica Neue',Arial,sans-serif;font-size:${size==='large'?'14pt':'9pt'};font-weight:300;color:#1a1a1a;letter-spacing:6px;text-transform:uppercase;margin:${size==='large'?'4mm':'2mm'} 0}
      .qr-line{width:30px;height:2px;background:#1a1a1a;margin:${size==='large'?'3mm':'2mm'} auto}
      .qr-txt{font-family:'Helvetica Neue',Arial,sans-serif;font-size:${size==='large'?'7pt':'5.5pt'};color:#999;line-height:1.5;font-weight:300;letter-spacing:0.5px}
      .qr-txt b{color:#333;font-weight:500;display:block;font-size:${size==='large'?'8pt':'6pt'}}
      .qr-url{font-size:${size==='large'?'6pt':'5pt'};color:#ccc;margin-top:auto;letter-spacing:2px}
    `,
    html: (t, num) => `<div class="qr-inner"><img src="/logo-taverne.png" class="qr-logo"/><div class="qr-wrap" id="svg-t${num}"></div><div class="qr-line"></div><div class="qr-tnum">Table ${num}</div><div class="qr-txt"><b>Scannez pour commander</b>Ouvrez l'appareil photo</div><div class="qr-url">latavernecay.fr</div></div>`,
  },
  premium: {
    label: 'Premium',
    desc: 'Noir et or, luxueux',
    preview: 'bg-[#0d0d0d]',
    qrColor: '#d4a852',
    css: (size) => `
      .qr-cell{width:${size==='large'?'95mm':'63mm'};height:${size==='large'?'133mm':'88mm'};border:1px dashed #555;display:flex;align-items:center;justify-content:center;page-break-inside:avoid;break-inside:avoid}
      .qr-inner{display:flex;flex-direction:column;align-items:center;text-align:center;padding:${size==='large'?'6mm':'4mm'};width:100%;height:100%;background:#0d0d0d;border:1px solid #d4a852;position:relative}
      .qr-inner::before{content:'';position:absolute;inset:2mm;border:1px solid rgba(212,168,82,0.2)}
      .qr-logo{height:${size==='large'?'14mm':'9mm'};margin:${size==='large'?'5mm':'3mm'} 0;filter:sepia(1) saturate(3) brightness(1.2) hue-rotate(10deg)}
      .qr-ornament{color:#d4a852;font-size:${size==='large'?'10pt':'7pt'};letter-spacing:6px;opacity:0.6}
      .qr-wrap{border:1px solid #d4a852;border-radius:4px;padding:${size==='large'?'3mm':'2mm'};margin:${size==='large'?'3mm':'2mm'} 0;background:white}
      .qr-wrap svg{width:${size==='large'?'28mm':'18mm'}!important;height:${size==='large'?'28mm':'18mm'}!important}
      .qr-tnum{font-family:Georgia,serif;font-size:${size==='large'?'22pt':'14pt'};font-weight:bold;color:#d4a852;margin:${size==='large'?'2mm':'1mm'} 0;letter-spacing:2px}
      .qr-txt{font-size:${size==='large'?'7pt':'5.5pt'};color:rgba(255,255,255,0.5);line-height:1.4;font-family:Georgia,serif}
      .qr-txt b{color:#d4a852;font-size:${size==='large'?'9pt':'6.5pt'};display:block;letter-spacing:1px}
      .qr-url{font-size:${size==='large'?'6pt':'5pt'};color:rgba(212,168,82,0.3);margin-top:auto;letter-spacing:2px}
    `,
    html: (t, num) => `<div class="qr-inner"><div class="qr-ornament">&#9830; &#9830; &#9830;</div><img src="/logo-taverne.png" class="qr-logo"/><div class="qr-ornament">&#9830; &#9830; &#9830;</div><div class="qr-wrap" id="svg-t${num}"></div><div class="qr-tnum">Table ${num}</div><div class="qr-txt"><b>Scannez pour commander</b>Ouvrez l'appareil photo</div><div class="qr-url">latavernecay.fr</div></div>`,
  },
  tropical: {
    label: 'Tropical',
    desc: 'Couleurs Guyane, chaleureux',
    preview: 'bg-gradient-to-b from-[#1b4332] to-[#2d6a4f]',
    qrColor: '#1b4332',
    css: (size) => `
      .qr-cell{width:${size==='large'?'95mm':'63mm'};height:${size==='large'?'133mm':'88mm'};border:1px dashed #bbb;display:flex;align-items:center;justify-content:center;page-break-inside:avoid;break-inside:avoid}
      .qr-inner{display:flex;flex-direction:column;align-items:center;text-align:center;padding:${size==='large'?'6mm':'4mm'};width:100%;height:100%;background:linear-gradient(180deg,#1b4332 0%,#2d6a4f 40%,#40916c 100%);border-radius:4px}
      .qr-logo{height:${size==='large'?'14mm':'9mm'};margin:${size==='large'?'4mm':'2mm'} 0;filter:brightness(2)}
      .qr-leaf{font-size:${size==='large'?'12pt':'8pt'};color:rgba(255,255,255,0.2);letter-spacing:4px}
      .qr-wrap{background:white;border-radius:10px;padding:${size==='large'?'3mm':'2mm'};margin:${size==='large'?'3mm':'2mm'} 0;box-shadow:0 2px 8px rgba(0,0,0,0.3)}
      .qr-wrap svg{width:${size==='large'?'28mm':'18mm'}!important;height:${size==='large'?'28mm':'18mm'}!important}
      .qr-tnum{font-family:Georgia,serif;font-size:${size==='large'?'22pt':'14pt'};font-weight:bold;color:#f4d35e;margin:${size==='large'?'2mm':'1mm'} 0;text-shadow:0 1px 3px rgba(0,0,0,0.3)}
      .qr-txt{font-size:${size==='large'?'8pt':'6pt'};color:rgba(255,255,255,0.75);line-height:1.4}
      .qr-txt b{color:white;font-size:${size==='large'?'9pt':'7pt'};display:block}
      .qr-url{font-size:${size==='large'?'6pt':'5pt'};color:rgba(255,255,255,0.25);margin-top:auto;padding-top:1mm}
    `,
    html: (t, num) => `<div class="qr-inner"><div class="qr-leaf">&#10048; &#10048; &#10048;</div><img src="/logo-taverne.png" class="qr-logo"/><div class="qr-leaf">&#10048; &#10048; &#10048;</div><div class="qr-wrap" id="svg-t${num}"></div><div class="qr-tnum">Table ${num}</div><div class="qr-txt"><b>Scannez pour commander</b>Ouvrez l'appareil photo</div><div class="qr-url">latavernecay.fr</div></div>`,
  },
  bienvenue: {
    label: 'Bienvenue',
    desc: 'Plaque doree, accueil',
    preview: 'bg-[#c5993f]',
    qrColor: '#1a1a1a',
    css: (size) => `
      .qr-cell{width:${size==='large'?'95mm':'63mm'};height:${size==='large'?'133mm':'88mm'};border:1px dashed #bbb;display:flex;align-items:center;justify-content:center;page-break-inside:avoid;break-inside:avoid}
      .qr-inner{display:flex;flex-direction:column;align-items:center;text-align:center;padding:${size==='large'?'6mm':'4mm'};width:100%;height:100%;background:linear-gradient(145deg,#d4a84a 0%,#c5993f 50%,#b8892e 100%);border:2px solid #a07828;border-radius:3px}
      .qr-icon svg{width:${size==='large'?'20mm':'13mm'};height:${size==='large'?'14mm':'9mm'}}
      .qr-title{font-family:Georgia,serif;font-size:${size==='large'?'24pt':'15pt'};font-weight:bold;color:#1a1a1a;letter-spacing:3px;margin:${size==='large'?'3mm':'2mm'} 0}
      .qr-subtitle{font-family:Georgia,serif;font-size:${size==='large'?'9pt':'6.5pt'};color:#2a2a0a;line-height:1.5;margin:${size==='large'?'2mm':'1mm'} 0}
      .qr-wrap{margin:${size==='large'?'4mm':'2mm'} 0}
      .qr-wrap svg{width:${size==='large'?'32mm':'20mm'}!important;height:${size==='large'?'32mm':'20mm'}!important}
      .qr-tnum{font-family:Georgia,serif;font-size:${size==='large'?'14pt':'9pt'};font-weight:bold;color:#1a1a1a;margin:${size==='large'?'2mm':'1mm'} 0}
      .qr-url{font-size:${size==='large'?'6pt':'5pt'};color:rgba(0,0,0,0.35);margin-top:auto;padding-top:1mm}
    `,
    html: (t, num) => `<div class="qr-inner"><div class="qr-icon"><svg viewBox="0 0 120 65" fill="#1a1a1a"><rect x="18" y="2" width="2.5" height="20" rx="1"/><rect x="23" y="2" width="2.5" height="20" rx="1"/><rect x="28" y="2" width="2.5" height="20" rx="1"/><path d="M17 22c0 7 4 11 8 12v26h2V34c4-1 8-5 8-12H17z"/><circle cx="60" cy="34" r="22" fill="none" stroke="#1a1a1a" stroke-width="3.5"/><circle cx="60" cy="34" r="9"/><path d="M93 2c7 0 11 9 9 22l-4 34h-4l-4-34c-2-13 2-22 9-22z"/></svg></div><div class="qr-title">BIENVENUE</div><div class="qr-subtitle">Merci de scanner le<br/>QR code pour voir notre menu</div><div class="qr-wrap" id="svg-t${num}"></div><div class="qr-tnum">Table ${num}</div><div class="qr-url">latavernecay.fr</div></div>`,
  },
};

const QRCodesPage = () => {
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState('classique');
  const [printSize, setPrintSize] = useState('large');
  const [aiTemplates, setAiTemplates] = useState([]);
  const [aiTheme, setAiTheme] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('staffToken');
    if (!token) { navigate('/staff/login'); return; }
    fetchTables();
    fetchAiTemplates();
  }, [navigate]);

  const fetchTables = async () => {
    try {
      const res = await api.get('/tables');
      setTables(res.data.filter(t => t.active).sort((a, b) => a.table_number - b.table_number));
    } catch { toast({ title: 'Erreur', variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  const fetchAiTemplates = async () => {
    try {
      const res = await api.get('/ai-templates/');
      setAiTemplates(res.data);
    } catch { /* IA optionnelle */ }
  };

  const generateAiTemplate = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/ai-templates/generate', { theme: aiTheme.trim() || undefined });
      await api.post('/ai-templates/save', res.data);
      toast({ title: `Template "${res.data.label}" genere` });
      setAiTheme('');
      await fetchAiTemplates();
      setTemplate(aiTemplateKey(res.data.template_id));
    } catch (err) {
      toast({
        title: 'Generation IA impossible',
        description: err.response?.data?.detail || 'Verifiez OPENROUTER_API_KEY sur le serveur',
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  const deleteAiTemplate = async (templateId, event) => {
    event.stopPropagation();
    try {
      await api.delete(`/ai-templates/${templateId}`);
      if (template === aiTemplateKey(templateId)) setTemplate('classique');
      await fetchAiTemplates();
      toast({ title: 'Template IA supprime' });
    } catch {
      toast({ title: 'Erreur suppression', variant: 'destructive' });
    }
  };

  const tpl = resolveTemplate(template, TEMPLATES, aiTemplates);
  const aiData = tpl.aiData;

  const printAll = () => {
    const cols = printSize === 'large' ? 2 : 3;
    const cardsHtml = tables.map(t => `<div class="qr-cell">${tpl.html(t, t.table_number)}</div>`).join('');
    const svgData = tables.map(t => {
      const el = document.getElementById(`qr-preview-${t.table_number}`);
      const svg = el?.querySelector('[data-qr] svg') || el?.querySelector('svg');
      return { num: t.table_number, svg: svg?.outerHTML || '' };
    });
    const gridCss = `
      @page{size:A4 portrait;margin:${printSize === 'large' ? '10mm' : '7mm'}}
      *{box-sizing:border-box;margin:0;padding:0}body{background:white}
      .page-grid{display:grid;grid-template-columns:repeat(${cols},1fr);gap:0;width:100%}
      ${tpl.css(printSize === 'large' ? 'large' : 'small')}
    `;
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><style>${gridCss}</style></head><body><div class="page-grid">${cardsHtml}</div></body></html>`);
    w.document.close();
    w.onload = () => {
      svgData.forEach(s => {
        const wrap = w.document.getElementById('svg-t' + s.num);
        if (wrap) wrap.innerHTML = s.svg;
      });
      setTimeout(() => w.print(), 400);
    };
  };

  const printSingle = (tableNum) => {
    const singleCss = `
      @page{size:A5;margin:0}*{box-sizing:border-box;margin:0;padding:0}body{background:white;display:flex;align-items:center;justify-content:center;min-height:100vh}
      .qr-cell{width:148mm;height:210mm;display:flex;align-items:center;justify-content:center}
      ${tpl.css('large').replace(/95mm/g,'148mm').replace(/133mm/g,'210mm').replace(/30mm/g,'55mm').replace(/20pt/g,'36pt').replace(/16mm/g,'25mm').replace(/14mm/g,'22mm').replace(/9pt/g,'14pt').replace(/8pt/g,'12pt').replace(/7pt/g,'10pt')}
    `;
    const html = `<div class="qr-cell">${tpl.html(null, tableNum)}</div>`;
    const el = document.getElementById(`qr-preview-${tableNum}`);
    const svg = el?.querySelector('[data-qr] svg') || el?.querySelector('svg');
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><style>${singleCss}</style></head><body>${html}</body></html>`);
    w.document.close();
    w.onload = () => {
      const wrap = w.document.getElementById('svg-t' + tableNum);
      if (wrap && svg) wrap.innerHTML = svg.outerHTML;
      setTimeout(() => w.print(), 300);
    };
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen">Chargement...</div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f1ed] to-white">
      <div className="bg-[#3d2817] text-white py-4 shadow-lg">
        <div className="max-w-6xl mx-auto px-4 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold" data-testid="qr-codes-title">QR Codes - Tables</h1>
            <p className="text-xs text-gray-300 mt-0.5">{tables.length} tables actives</p>
          </div>
          <div className="flex gap-2 items-center">
            <Button onClick={printAll} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs" data-testid="print-all-btn">
              <Printer size={14} className="mr-1" /> Imprimer tout (A4)
            </Button>
            <Button onClick={() => navigate('/staff/dashboard')} size="sm" className="bg-red-600 hover:bg-red-700 text-xs">
              <ArrowLeft size={14} className="mr-1" /> Retour
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-stone-600 mb-3 uppercase tracking-wider">Choisir le design</h2>
          <div className="flex gap-3 flex-wrap">
            {Object.entries(TEMPLATES).map(([key, t]) => (
              <button key={key} onClick={() => setTemplate(key)} data-testid={`tpl-${key}`}
                className={`relative flex flex-col items-center p-3 rounded-xl border-2 transition-all w-28
                  ${template === key ? 'border-amber-500 shadow-lg scale-105 bg-amber-50' : 'border-stone-200 hover:border-stone-300 bg-white'}`}>
                <div className={`w-14 h-20 rounded-lg ${t.preview} mb-2 flex items-center justify-center shadow-inner`}>
                  <span className="text-white text-lg font-bold" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)', color: key === 'minimaliste' ? '#1a1a1a' : 'white' }}>QR</span>
                </div>
                <span className="text-xs font-semibold text-stone-700">{t.label}</span>
                <span className="text-[10px] text-stone-400">{t.desc}</span>
                {template === key && <div className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center"><Eye size={10} className="text-white" /></div>}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-6 p-4 rounded-xl border border-violet-200 bg-violet-50/50" data-testid="ai-templates-section">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div>
              <h2 className="text-sm font-semibold text-violet-900 uppercase tracking-wider flex items-center gap-2">
                <Sparkles size={16} /> Templates IA (OpenRouter gratuit)
              </h2>
              <p className="text-xs text-violet-700 mt-1">Generez des designs uniques via IA, sauvegardes automatiquement</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            <input
              value={aiTheme}
              onChange={e => setAiTheme(e.target.value)}
              placeholder="Theme optionnel (ex: coucher de soleil caraibe)"
              className="flex-1 min-w-[220px] px-3 py-2 text-sm border border-violet-200 rounded-lg bg-white"
              data-testid="ai-theme-input"
            />
            <Button
              onClick={generateAiTemplate}
              disabled={generating}
              className="bg-violet-700 hover:bg-violet-800 text-xs"
              data-testid="generate-ai-template-btn"
            >
              {generating ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Sparkles size={14} className="mr-1" />}
              Generer un design
            </Button>
          </div>
          {aiTemplates.length > 0 ? (
            <div className="flex gap-3 flex-wrap">
              {aiTemplates.map(t => {
                const key = aiTemplateKey(t.template_id);
                return (
                  <button
                    key={key}
                    onClick={() => setTemplate(key)}
                    data-testid={`tpl-${key}`}
                    className={`relative flex flex-col items-center p-3 rounded-xl border-2 transition-all w-28
                      ${template === key ? 'border-violet-500 shadow-lg scale-105 bg-violet-50' : 'border-stone-200 hover:border-violet-300 bg-white'}`}
                  >
                    <div
                      className="w-14 h-20 rounded-lg mb-2 flex items-center justify-center shadow-inner border"
                      style={{ background: t.bg, borderColor: t.border, color: t.text }}
                    >
                      <span className="text-lg font-bold" style={{ color: t.accent }}>IA</span>
                    </div>
                    <span className="text-xs font-semibold text-stone-700 truncate w-full text-center">{t.label}</span>
                    <span className="text-[10px] text-stone-400 truncate w-full text-center">{t.desc}</span>
                    <button
                      type="button"
                      onClick={e => deleteAiTemplate(t.template_id, e)}
                      className="absolute top-1 right-1 p-1 rounded-full bg-red-100 text-red-600 hover:bg-red-200"
                      data-testid={`delete-ai-${t.template_id}`}
                      title="Supprimer"
                    >
                      <Trash2 size={10} />
                    </button>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-violet-600">Aucun template IA sauvegarde. Cliquez sur Generer pour en creer un.</p>
          )}
        </div>

        <div className="mb-6 flex items-center gap-4">
          <span className="text-sm font-semibold text-stone-600">Taille par page :</span>
          <div className="flex bg-stone-100 rounded-lg overflow-hidden text-xs">
            <button onClick={() => setPrintSize('large')} data-testid="size-large"
              className={`px-4 py-2 font-semibold transition-colors ${printSize === 'large' ? 'bg-[#2c1810] text-white' : 'text-stone-600 hover:bg-stone-200'}`}>
              4 par page (grand)
            </button>
            <button onClick={() => setPrintSize('small')} data-testid="size-small"
              className={`px-4 py-2 font-semibold transition-colors ${printSize === 'small' ? 'bg-[#2c1810] text-white' : 'text-stone-600 hover:bg-stone-200'}`}>
              9 par page (petit)
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {tables.map(table => {
            const orderUrl = `${FRONTEND_URL}/order/${table.qr_code}`;
            const isAi = Boolean(aiData);
            const isArdoise = !isAi && template === 'ardoise';
            const isPremium = !isAi && template === 'premium';
            const isTropical = !isAi && template === 'tropical';
            const isMinimaliste = !isAi && template === 'minimaliste';
            const isBienvenue = !isAi && template === 'bienvenue';
            const bgClass = isAi ? '' : isBienvenue ? 'border-[#a07828]' : isArdoise ? 'bg-[#2a2a2a]' : isPremium ? 'bg-[#0d0d0d]' : isTropical ? 'bg-gradient-to-b from-[#1b4332] to-[#40916c]' : isMinimaliste ? 'bg-white border-stone-200' : 'bg-white border-[#d4c4b0]';
            const textColor = isAi ? '' : (isArdoise || isPremium || isTropical) ? 'text-white' : 'text-[#2c1810]';
            const goldColor = isPremium ? 'text-[#d4a852]' : '';
            const trophyColor = isTropical ? 'text-[#f4d35e]' : '';
            const cardStyle = isAi ? { background: aiData.bg, borderColor: aiData.border, color: aiData.text, minHeight: '280px' } : isBienvenue ? { minHeight: '280px', background: 'linear-gradient(145deg, #d4a84a 0%, #c5993f 50%, #b8892e 100%)' } : { minHeight: '280px' };
            return (
              <div key={table.table_number} className="group" data-testid={`qr-card-${table.table_number}`}>
                <div id={`qr-preview-${table.table_number}`}
                  className={`rounded-xl border-2 shadow-md flex flex-col items-center py-6 px-4 relative overflow-hidden ${bgClass}`}
                  style={cardStyle}>
                  {isPremium && !isAi && <div className="absolute inset-[3px] border border-[#d4a852]/20 pointer-events-none rounded-lg" />}
                  {isAi ? (
                    <>
                      {aiData.deco_top && <div className="text-sm opacity-75 mb-1" style={{ color: aiData.accent }}>{aiData.deco_top}</div>}
                      {aiData.title ? (
                        <div className="text-lg font-bold tracking-wider mb-1" style={{ color: aiData.accent, fontFamily: aiData.font }}>{aiData.title}</div>
                      ) : (
                        <img src="/logo-taverne.png" alt="La Taverne" className="mx-auto mb-2" style={{ height: '40px', filter: aiData.dark ? 'brightness(1.8)' : 'none' }} />
                      )}
                      {aiData.subtitle && <div className="text-[10px] text-center leading-relaxed mb-2 opacity-90" style={{ fontFamily: aiData.font }}>{aiData.subtitle}</div>}
                    </>
                  ) : isBienvenue ? (
                    <>
                      <svg viewBox="0 0 120 65" fill="#1a1a1a" style={{ width: '55px', height: '38px', marginBottom: '6px' }}>
                        <rect x="18" y="2" width="2.5" height="20" rx="1"/>
                        <rect x="23" y="2" width="2.5" height="20" rx="1"/>
                        <rect x="28" y="2" width="2.5" height="20" rx="1"/>
                        <path d="M17 22c0 7 4 11 8 12v26h2V34c4-1 8-5 8-12H17z"/>
                        <circle cx="60" cy="34" r="22" fill="none" stroke="#1a1a1a" strokeWidth="3.5"/>
                        <circle cx="60" cy="34" r="9"/>
                        <path d="M93 2c7 0 11 9 9 22l-4 34h-4l-4-34c-2-13 2-22 9-22z"/>
                      </svg>
                      <div className="text-lg font-bold text-[#1a1a1a] tracking-wider" style={{ fontFamily: 'Georgia, serif' }}>BIENVENUE</div>
                      <div className="text-[10px] text-[#2a2a0a] text-center leading-relaxed mt-1" style={{ fontFamily: 'Georgia, serif' }}>
                        Merci de scanner le<br/>QR code pour voir notre menu
                      </div>
                    </>
                  ) : (
                    <>
                      <img src="/logo-taverne.png" alt="La Taverne" className="mx-auto mb-2"
                        style={{ height: '40px', filter: (isArdoise || isTropical) ? 'brightness(2)' : isPremium ? 'sepia(1) saturate(3) brightness(1.2) hue-rotate(10deg)' : isMinimaliste ? 'opacity(0.7)' : 'none' }} />
                      {isMinimaliste ? null : <div className={`w-12 h-px ${isArdoise ? 'bg-white/20' : isPremium ? 'bg-[#d4a852]/30' : isTropical ? 'bg-white/20' : 'bg-[#d4c4b0]'} my-1`} />}
                    </>
                  )}
                  <div data-qr className={`p-2 rounded-lg my-2 ${isAi ? 'bg-white shadow-md' : isBienvenue ? '' : (isArdoise || isTropical) ? 'bg-white shadow-lg' : isPremium ? 'bg-white border border-[#d4a852]' : isMinimaliste ? '' : 'border-2 border-[#2c1810]'}`}>
                    <QRCodeSVG value={orderUrl} size={90} level="H" fgColor={tpl.qrColor} bgColor="white" />
                  </div>
                  <div className={`text-xl font-bold mt-1 ${isAi ? '' : isBienvenue ? 'text-[#1a1a1a]' : goldColor || trophyColor || textColor}`} style={{ fontFamily: isAi ? aiData.font : isMinimaliste ? 'Helvetica,sans-serif' : 'Georgia,serif', letterSpacing: isMinimaliste ? '4px' : '0', fontWeight: isMinimaliste ? 300 : 700, color: isAi ? aiData.accent : undefined }}>
                    Table {table.table_number}
                  </div>
                  {(isAi || !isBienvenue) && (
                    <div className={`text-center mt-1 text-xs leading-relaxed ${isArdoise ? 'text-white/70' : isPremium ? 'text-white/50' : isTropical ? 'text-white/75' : isMinimaliste ? 'text-stone-400' : 'text-stone-500'}`}>
                      <strong className={`block text-xs ${goldColor || trophyColor || textColor}`}>Scannez pour commander</strong>
                    </div>
                  )}
                  <div className={`text-[9px] mt-auto pt-2 ${isAi ? '' : isBienvenue ? 'text-black/30' : isArdoise ? 'text-white/20' : isPremium ? 'text-[#d4a852]/30' : isTropical ? 'text-white/20' : isMinimaliste ? 'text-stone-300' : 'text-stone-300'}`} style={isAi ? { opacity: 0.5 } : undefined}>latavernecay.fr</div>
                </div>
                <div className="flex justify-center mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button onClick={() => printSingle(table.table_number)} variant="outline" size="sm" className="text-xs" data-testid={`print-table-${table.table_number}`}>
                    <Printer size={12} className="mr-1" /> Imprimer
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default QRCodesPage;
